import { randomBetween } from "./Utils/Language/SeededRandom.js";
import Vector3 from "./Utils/Vector3";
import Prototype from "./Prototype/Prototype";
import PrototypeManager from "./Prototype/PrototypeManager";

/**
 * Cell = tilePosition in the 3D matrix that represents the map.
 */
class Cell {
  /**
   * Cell coordinates in the map
   * @type {Boolean}
   * @private
   */
  private readonly _coordinates: Vector3;

  /**
   * Empty if collapsed
   * @type {Prototype[]}
   * @private
   */
  private _possiblePrototypes: Prototype[];

  /**
   * isCollapsed
   * @type {boolean}
   * @public
   */
  private _collapsed: boolean = false;

  /**
   * Collasped cell prototype
   * @private
   */
  private _prototypeId: string | null = null;

  constructor(coordinates: Vector3, prototypeManager: PrototypeManager) {
    this._coordinates = coordinates;
    this._possiblePrototypes = prototypeManager.prototypes;
  }

  /**
   * Collapse the cell with a prototype id
   * @param {string} prototypeId to force the collapse to a specific prototype
   * @returns {Cell} the collapsed cell
   */
  collapse(protoId: string | null = null): Cell {
    let theChosenOne: string;
    if (protoId) {
      theChosenOne = protoId;
    } else {
      theChosenOne = this.getWeightedRandomPrototype();
    }
    this._possiblePrototypes = [];
    this._prototypeId = theChosenOne;
    if (!theChosenOne) {
      // console.log(this);
      throw new Error("No prototype chosen");
    }
    this._collapsed = true;
    return this; // TODO : remove, debug only
  }

  /**
   * Get all prototypes with the biggest weight
   * @returns {string[]}
   */
  getBiggestWeightPrototypes(): string[] {
    const maxWeight = Math.max(
      ...this._possiblePrototypes.map((prototype) => prototype.weight),
    );
    return this._possiblePrototypes
      .filter((prototype) => prototype.weight === maxWeight)
      .map((prototype) => prototype.id);
  }

  /**
   * Get weighted random prototype
   */
  getWeightedRandomPrototype(): string {
    const weightedArray: string[] = this._possiblePrototypes.flatMap(
      (prototype) => Array(prototype.weight).fill(prototype.id),
    );
    return weightedArray[randomBetween(0, weightedArray.length - 1)];
  }

  /**
   * Get the entropy of the cell
   * @returns {number}
   */
  getEntropy(): number {
    // TODO : check if necessary
    if (this._collapsed) return Infinity;

    return this._possiblePrototypes.length;
  }

  /**
   * constrain the cell
   * @param {String} prototypeId
   */
  constrain(prototypeId: string): void {
    this._possiblePrototypes = this._possiblePrototypes.filter(
      (prototype) => prototype.id !== prototypeId,
    );
  }

  //// GETTERS

  get coordinates(): Vector3 {
    return this._coordinates;
  }

  get collapsed(): boolean {
    return this._collapsed;
  }

  get possiblePrototypesIds(): string[] {
    return this._possiblePrototypes.map((prototype) => prototype.id);
  }

  get possiblePrototypes(): Prototype[] {
    return this._possiblePrototypes;
  }

  get collapsedCellPrototypeId(): string {
    if (this._collapsed && this._prototypeId) {
      return this._prototypeId;
    } else {
      if (!this._collapsed) throw new Error("Cell is not collapsed");
      throw new Error("Cell has no prototype id");
    }
  }
}

export default Cell;
